import { useContext, useMemo, useState } from 'react';

import { Button, Icon } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useFormikContext } from 'formik';
import { RiSendPlaneFill } from 'react-icons/ri';
import { toast } from 'react-toastify';

import { Deal } from '../../../../gql/dealGql';
import {
  mapProcessorToTTProcessor,
  mapTTProcessorToProcessor,
} from '../../../../gql/financialInfoGql';
import {
  CreateTransactionInput,
  StateAbbreviation,
  TitleRegistrationOption,
  TtProductType,
  useCreateTransactionMutation,
  useOnTransactionCreatedSubscription,
} from '../../../../gql/generated/graphql';
import { getCreateTransactionCallbackUrl } from '../../../../gql/taterTitleGql';

import Tooltip from '../../../shared/Tooltip';

import { isCanada } from '../../../../constants/countries';
import { useGetUpdatedPayoffValues } from '../../../../hooks/useGetUpdatedPayoffValues';
import { DealContext } from '../../../../libs/DealContext';
import { logger } from '../../../../libs/Logger';
import { getBothAddressLines } from '../../../../utils/addresses';
import { mapDealToGetTTFeesVariables } from '../../../../utils/deals';
import { CUSTOM_DATE_FIELDS } from '../../constants';

export const SendToProcessorButton = () => {
  const {
    deal,
    jurisdictionData: { jurisdiction, conditions },
  } = useContext(DealContext);

  const { values, setFieldValue } = useFormikContext<Deal>();

  const [processingCreateTransaction, setProcessingCreateTransaction] = useState<boolean>(false);

  const [createTransactionMutation] = useCreateTransactionMutation();
  const { getUpdatedPayoffValues } = useGetUpdatedPayoffValues();

  const alreadySent = !!values.financial_info.tt_transaction_id;
  const buttonText = alreadySent
    ? 'SENT'
    : `SEND TO ${values.financial_info.processor?.toUpperCase() || 'PROCESSOR'}`;

  const supportedProcessors = useMemo(
    () => jurisdiction?.supportedProcessors?.map(mapTTProcessorToProcessor) ?? [],
    [jurisdiction?.supportedProcessors],
  );

  const allErrors = {
    ...(!deal.id ? { dealId: 'Deal ID is required' } : {}),
    ...(isCanada(deal.country) ? { country: 'Not available for Canadian deals' } : {}),
    ...(!deal.customer?.address?.state ? { addressState: 'State is required' } : {}),
    ...(!values.financial_info.processor ? { processor: 'Processor is required' } : {}),
    ...(values.financial_info.processor &&
    !supportedProcessors.includes(values.financial_info.processor)
      ? { processor: 'Processor is not supported' }
      : {}),
  };
  const canSendToProcessor = Object.keys(allErrors).length === 0;

  useOnTransactionCreatedSubscription({
    skip: !canSendToProcessor,
    variables: {
      dealId: deal.id ?? 0,
    },
    onData: ({ data }) => {
      if (!data?.data?.onTransactionCreated?.tt_transaction_id) {
        return;
      }

      setFieldValue(
        'financial_info.tt_transaction_id',
        data.data.onTransactionCreated.tt_transaction_id,
      );

      setProcessingCreateTransaction(false);
      toast.success(`Sent to ${values.financial_info.processor} successfully`);
    },
  });

  const handleClick = async () => {
    setProcessingCreateTransaction(true);
    let input: CreateTransactionInput | undefined;
    try {
      const { totalPayoffWithoutSalesTax } = await getUpdatedPayoffValues({
        data: values,
        userEnteredTotalPayoff: values.car.payoff.user_entered_total_payoff ?? 0,
      });

      input = {
        state: values.customer.address.state as StateAbbreviation,
        types:
          values.financial_info.title_registration_option === TitleRegistrationOption.TitleOnly
            ? [TtProductType.Title]
            : [TtProductType.Title, TtProductType.Registration],
        parameters: {
          ...mapDealToGetTTFeesVariables(values, { ttEndpoint: 'createTransaction' }),
          middleName: values.customer.middle_name,
          suffix: values.customer.name_suffix,
          dob: values.customer.dob,
          email: values.customer.email ?? '',
          mailingAddressLine1: values.customer.mailing_address?.address_line,
          mailingAddressLine2: values.customer.mailing_address?.address_line_2,
          mailingCity: values.customer.mailing_address?.city,
          mailingState: values.customer.mailing_address?.state as StateAbbreviation | undefined,
          mailingZipCode: values.customer.mailing_address?.zip,
          mailingCounty: values.customer.mailing_address?.county,
          color: values.car.color,
          trim: values.car.kbb_trim_name,
          address: getBothAddressLines(values.customer.address),
          estimatedPayoff: values.car.payoff.estimated_payoff ?? values.car.payoff.vehicle_payoff,
          payoffWithoutSalesTax: totalPayoffWithoutSalesTax ?? 0,
          salesTax: values.car.payoff.sales_tax_from_payoff ?? 0,
          payoffIncludesSalesTax: values.car.payoff.payoff_includes_sales_tax ?? false,
          titleRegistrationOption: values.financial_info.title_registration_option,
          warranty: undefined,
          odometerReading: values.car.mileage,
          transferringPlates: conditions?.transferringPlates,
          dealId: values.id?.toString(),
          insuranceCompanyName: values.customer.proof_of_insurance?.company_name,
          insurancePolicyNumber: values.customer.proof_of_insurance?.policy_number,
          insurancePolicyExpirationDate: values.customer.proof_of_insurance?.expires
            ? format(new Date(values.customer.proof_of_insurance?.expires), 'yyyy-MM-dd')
            : undefined,
          // Cobuyer fields
          cobuyerAddress: getBothAddressLines(values.cobuyer?.address ?? {}),
          cobuyerCity: values.cobuyer?.address?.city,
          cobuyerFirstName: values.cobuyer?.first_name,
          cobuyerLastName: values.cobuyer?.last_name,
          cobuyerMiddleName: values.cobuyer?.middle_name,
          cobuyerSuffix: values.cobuyer?.name_suffix,
          cobuyerState: values.cobuyer?.address?.state as StateAbbreviation | undefined,
          cobuyerZip: values.cobuyer?.address?.zip,
          cobuyerCounty: values.cobuyer?.address?.county,
          cobuyerDob: values.cobuyer?.dob,
          cobuyerEmail: values.cobuyer?.email,
          cobuyerPhone: values.cobuyer?.phone_number,
          // New Lienholder fields
          newBank: values.financial_info.new_lienholder?.name,
          newBankAddress: values.financial_info.new_lienholder?.address,
          newBankCity: values.financial_info.new_lienholder?.city,
          newBankState: values.financial_info.new_lienholder?.state as
            | StateAbbreviation
            | undefined,
          newBankZip: values.financial_info.new_lienholder?.zip,
          signedDate: format(
            new Date(
              values.deal_dates?.custom_dates?.signed ??
                values.deal_dates?.dates?.signed ??
                new Date().toISOString(),
            ),
            'yyyy-MM-dd',
          ),
        },
        conditions,
        processor: mapProcessorToTTProcessor(values.financial_info.processor),
        callbackUrl: getCreateTransactionCallbackUrl(),
      };

      await createTransactionMutation({
        variables: {
          input,
        },
      });
      setFieldValue(CUSTOM_DATE_FIELDS.customSentToAuditField, new Date().toISOString());
    } catch (e) {
      const error = e as Error;

      if (input != null) {
        const sanitizedInput = {
          state: input.state,
          types: input.types,
          processor: input.processor,
          callbackUrl: input.callbackUrl,
          parameters: {
            dealId: input.parameters?.dealId,
            vin: input.parameters?.vin,
            firstName: input.parameters?.firstName,
            lastName: input.parameters?.lastName,
            titleRegistrationOption: input.parameters?.titleRegistrationOption,
            payoffWithoutSalesTax: input.parameters?.payoffWithoutSalesTax,
          },
        };

        logger.error(
          'SendToProcessorButton.tsx',
          'Failed to create TT transaction',
          sanitizedInput,
          e,
        );
      } else {
        logger.error('SendToProcessorButton.tsx', 'Failed to get updated payoff values', e);
      }
      toast.error(`Error sending to ${values.financial_info.processor} - ${error.message}`);
    } finally {
      setProcessingCreateTransaction(false);
    }
  };

  return (
    <Tooltip errors={allErrors}>
      <Button
        isLoading={processingCreateTransaction}
        loadingText={buttonText}
        isDisabled={!canSendToProcessor || alreadySent}
        variant="secondary"
        size="sm"
        leftIcon={<Icon as={RiSendPlaneFill} fontSize="md" />}
        onClick={handleClick}
        mt={0}
      >
        {buttonText}
      </Button>
    </Tooltip>
  );
};
